<script lang="ts">
	import "./app.postcss";
	import { onMount } from "svelte";
	import {
		DarkMode,
		Navbar,
		NavBrand,
		NavHamburger,
		Sidebar,
		SidebarGroup,
		SidebarItem,
		SidebarWrapper,
		CloseButton,
		Alert,
		Spinner,
		Button,
		Heading,
		Img,
		P,
	} from "flowbite-svelte";
	import { sineIn } from "svelte/easing";

	import Router, { push, link, location } from "./svelte-spa-router";

	import routes from "./routes";

	import site from "./site";
	import { Icon } from "flowbite-svelte-icons";

	import { isConfigurationLoaded, configurationError } from "./AppConfiguration";
	import AbstractID, {
		isAuthorizationPending,
		isAuthorized,
		colorTheme,
	} from "./AbstractID";
	import Drawer from "./components/Drawer.svelte";
	import AbstractIdCard from "./components/AbstractIDCard.svelte";

	// Public status page is the anonymous root; admin lives under /Admin/*.
	$: isAdmin = $location.startsWith("/Admin");
	// The admin area requires the ADMIN role — being authenticated is not enough.
	// (Re-evaluates when authorization settles; tokenData is set by then.)
	$: hasAdminAccess = $isAuthorized && AbstractID.isUserApplicationAccessRoleAvailable && AbstractID.isAdministrator;
	let loginTriggered = false;

	function signOutToLogin() {
		window.localStorage.removeItem("jwt2");
		AbstractID.logout();
		setTimeout(() => (window.location.href = `${site.idBaseURL}/auth/verify?logout=true&app=${site.appID}&returnURL=${encodeURIComponent(site.baseURL)}`), 250);
	}
	// Entering the admin area while unauthenticated sends the user to AbstractID.
	$: if (isAdmin && !loginTriggered && !$isAuthorizationPending && !$isAuthorized) {
		loginTriggered = true;
		AbstractID.navigateToLogin();
	}

	const breakPoint: number = 640;
	let width: number = breakPoint;
	let isSidebarBackdropEnabled: boolean = true;
	let isSidebarHideOnClickOutside = true;
	let drawerHidden: boolean = false;

	$: if (width >= breakPoint) {
		drawerHidden = false;
		isSidebarHideOnClickOutside = false;
		isSidebarBackdropEnabled = false;
	} else {
		drawerHidden = true;
		isSidebarHideOnClickOutside = true;
		isSidebarBackdropEnabled = true;
	}
	onMount(() => {
		drawerHidden = width < breakPoint;
		isSidebarHideOnClickOutside = width < breakPoint;
	});

	const fnToggleSide = (event?: Event, href: any = undefined) => {
		if (width < breakPoint) drawerHidden = !drawerHidden;
		if (href) {
			event?.preventDefault();
			push(href);
		}
	};
	const fnToggleDrawer = () => { drawerHidden = false; };

	function toggleTheme() {
		$colorTheme = $colorTheme === "dark" ? "light" : "dark";
		window.document.documentElement.classList.toggle("dark", $colorTheme === "dark");
	}

	$: activeUrl = $location;

	const adminNav = [
		{ label: "Dashboard", href: "/Admin", icon: "home-outline" },
		{ label: "Services", href: "/Admin/Services", icon: "grid-outline" },
		{ label: "Probes", href: "/Admin/Probes", icon: "chart-line-up-outline" },
		{ label: "Metrics", href: "/Admin/Metrics", icon: "chart-mixed-outline" },
		{ label: "Incidents", href: "/Admin/Incidents", icon: "exclamation-circle-outline" },
		{ label: "Alerts", href: "/Admin/Alerts", icon: "bell-outline" },
		{ label: "Scaling", href: "/Admin/Scaling", icon: "layers-outline" },
	];
</script>

<svelte:window bind:innerWidth={width} />

{#if isAdmin}
	<!-- Authenticated admin shell (existing template layout) -->
	<header class="flex-none w-full mx-auto bg-white dark:bg-zinc-800">
		<Navbar class="bg-zinc-100 dark:bg-zinc-800 border-solid border-t-[4px] border-primary-500" hidden={!drawerHidden}>
			<NavHamburger on:click={fnToggleDrawer} />
			<NavBrand href="/Admin" class="lg:ml-64">
				<span class="text-xl font-bold dark:text-white">{site.name} Admin</span>
			</NavBrand>
			<div class="flex items-center ml-auto">
				<DarkMode class="inline-block text-black dark:hover:text-white hover:text-gray-900" />
			</div>
		</Navbar>
	</header>

	<Drawer
		transitionType="fly"
		divClass="overflow-y-auto z-40 bg-white text-white dark:bg-zinc-800"
		transitionParams={{ x: -250, duration: 200, easing: sineIn }}
		bind:backdrop={isSidebarBackdropEnabled}
		bind:hidden={drawerHidden}
		bind:activateClickOutside={isSidebarHideOnClickOutside}
		width="w-[250px]"
		class="overflow-scroll pb-32"
		id="sidebar"
	>
		<div class="flex items-center">
			<CloseButton on:click={() => (drawerHidden = true)} class="mt-2 -mb-4 text-white sm:hidden" />
		</div>
		<Sidebar asideClass="w-54" {activeUrl}>
			<SidebarWrapper divClass="overflow-y-auto py-4 px-4 rounded">
				{@const activeClass = "flex items-center p-2 text-base font-normal text-gray-900 rounded-lg dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 active"}
				{@const nonActiveClass = "flex items-center p-2 text-base font-normal text-gray-900 rounded-lg dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700"}
				{@const iconClass = "w-5 h-5 min-w-5 min-h-5 shrink-0 text-primary-500 dark:text-primary-500"}
				{@const labelClass = "ml-3 truncate"}

				<a title={site.name} href="/Admin" use:link class="block">
					<Img class="h-[36px] max-w-none dark:invert mx-auto mb-2" src="/LogoTextWide-Black.png" />
					<p class="tracking-wide font-bold uppercase text-xs text-center text-zinc-800 dark:text-zinc-300 rounded-md border-zinc-700 border">
						{site.shortname}
					</p>
				</a>

				<a title={site.name} href="/" use:link class="block mt-3 mb-3 text-center font-bold uppercase text-xs tracking-wide text-zinc-800 dark:text-zinc-300">
					← Public status page
				</a>

				{#if $isAuthorized}
					<AbstractIdCard compact on:click={() => {}} />
				{/if}

				{#if !$configurationError && hasAdminAccess}
					<SidebarGroup ulClass="mt-2 appNav">
						{#each adminNav as item}
							<SidebarItem {activeClass} {nonActiveClass} spanClass={labelClass} label={item.label} href={item.href} on:click={(event) => fnToggleSide(event, item.href)}>
								<svelte:fragment slot="icon"><Icon name={item.icon} class={iconClass} /></svelte:fragment>
							</SidebarItem>
						{/each}
					</SidebarGroup>

					<SidebarGroup ulClass="mt-0 appNav">
						<SidebarItem {activeClass} {nonActiveClass} spanClass={labelClass} label="Users" href="/Admin/Users" on:click={(event) => fnToggleSide(event, "/Admin/Users")}>
							<svelte:fragment slot="icon"><Icon name="users-outline" class={iconClass} /></svelte:fragment>
						</SidebarItem>
					</SidebarGroup>

					<SidebarGroup ulClass="mt-0 appNav">
						<SidebarItem {activeClass} {nonActiveClass} spanClass={labelClass} label="Settings" href="/Admin/Settings" on:click={(event) => fnToggleSide(event, "/Admin/Settings")}>
							<svelte:fragment slot="icon"><Icon name="cog-outline" class={iconClass} /></svelte:fragment>
						</SidebarItem>
					</SidebarGroup>
				{/if}
			</SidebarWrapper>
		</Sidebar>

		<div class="text-gray-600 dark:text-gray-500 text-sm text-center mt-8">
			<div class="flex justify-center gap-2 mb-4">
				<Button class="p-2" color="alternative" on:click={toggleTheme}>
					<Icon name={$colorTheme === "dark" ? "sun-solid" : "moon-solid"} class="w-4 h-4" />
				</Button>
			</div>
			<p>
				<a
					class="font-bold hover:underline text-gray-700 dark:text-gray-400 hover:text-black dark:hover:text-white"
					href="/"
				>{site.shortname}</a>
				is an
				<a
					class="font-bold hover:underline text-gray-700 dark:text-gray-400 hover:text-black dark:hover:text-white"
					href="https://abstract3d.com"
					target="_blank">Abstract</a>
				product.<br />All rights reserved.
			</p>

			<div>
				<a href="https://abstract3d.com" target="_blank">
					<img
						class="max-w-[48px] dark:invert opacity-70 dark:opacity-30 inline mt-4 hover:opacity-100 hover:dark:opacity-100 rounded-sm"
						src="/Abstract-LogoIcon-Black-Transparent.png"
						alt="Abstract"
					/>
				</a>
			</div>

			<div class="mt-4 flex justify-center gap-2">
				<a href="https://x.com/abstract3d" title="Follow Abstract on Twitter" target="_blank">
					<svg
						class="w-4 h-4 inline text-gray-500 dark:text-gray-600 hover:text-black dark:hover:text-white"
						aria-hidden="true"
						xmlns="http://www.w3.org/2000/svg"
						fill="none"
						viewBox="0 0 20 20"
					>
						<path
							fill="currentColor"
							d="M12.186 8.672 18.743.947h-2.927l-5.005 5.9-4.44-5.9H0l7.434 9.876-6.986 8.23h2.927l5.434-6.4 4.82 6.4H20L12.186 8.672Zm-2.267 2.671L8.544 9.515 3.2 2.42h2.2l4.312 5.719 1.375 1.828 5.731 7.613h-2.2l-4.699-6.237Z"
						/>
					</svg>
				</a>

				<a href="https://discord.gg/ra5cCvf52s" title="Join Abstract on Discord" target="_blank">
					<svg
						class="w-5 h-5 inline text-gray-500 dark:text-gray-600 hover:text-black dark:hover:text-white"
						aria-hidden="true"
						xmlns="http://www.w3.org/2000/svg"
						fill="none"
						viewBox="0 0 24 24"
					>
						<path
							fill="currentColor"
							d="M18.942 5.556a16.3 16.3 0 0 0-4.126-1.3 12.04 12.04 0 0 0-.529 1.1 15.175 15.175 0 0 0-4.573 0 11.586 11.586 0 0 0-.535-1.1 16.274 16.274 0 0 0-4.129 1.3 17.392 17.392 0 0 0-2.868 11.662 15.785 15.785 0 0 0 4.963 2.521c.41-.564.773-1.16 1.084-1.785a10.638 10.638 0 0 1-1.706-.83c.143-.106.283-.217.418-.331a11.664 11.664 0 0 0 10.118 0c.137.114.277.225.418.331-.544.328-1.116.606-1.71.832a12.58 12.58 0 0 0 1.084 1.785 16.46 16.46 0 0 0 5.064-2.595 17.286 17.286 0 0 0-2.973-11.59ZM8.678 14.813a1.94 1.94 0 0 1-1.8-2.045 1.93 1.93 0 0 1 1.8-2.047 1.918 1.918 0 0 1 1.8 2.047 1.929 1.929 0 0 1-1.8 2.045Zm6.644 0a1.94 1.94 0 0 1-1.8-2.045 1.93 1.93 0 0 1 1.8-2.047 1.919 1.919 0 0 1 1.8 2.047 1.93 1.93 0 0 1-1.8 2.045Z"
						/>
					</svg>
				</a>
			</div>
		</div>
	</Drawer>

	<div class="flex px-4 mx-0 max-w-screen-2xl w-full">
		<main class="mt-10 mb-40 sm:ml-64 xl:ml-72 w-full mx-auto dark:text-white">
			{#if !$isAuthorizationPending && $isConfigurationLoaded && hasAdminAccess}
				<Router {routes} restoreScrollState={true} on:conditionsFailed={() => push("/Admin")} />
			{:else if $isAuthorized && !$isAuthorizationPending && !hasAdminAccess}
				<Alert>
					<Heading tag="h1"><Icon name="lock-solid" class="inline w-12 h-12 pb-3 mr-2" />Access Denied</Heading>
					<P>
						The {site.name} admin area requires the <span class="font-mono font-semibold">{site.adminRole}</span> role.
						Please contact an administrator, or sign in with a different account.
					</P>
					<div class="mt-4 flex gap-2">
						<Button size="sm" on:click={signOutToLogin}>Sign out / switch account</Button>
						<Button size="sm" color="alternative" on:click={() => push("/")}>← Public status page</Button>
					</div>
				</Alert>
			{:else if ($configurationError ?? "").length > 0}
				<Alert>
					<Heading tag="h1"><Icon name="bug-solid" class="inline w-12 h-12 pb-3 mr-2" />No Backend Response</Heading>
					<P>{site.name} is currently unavailable. Please try again later.</P>
				</Alert>
			{:else}
				<Spinner />
			{/if}
		</main>
	</div>
{:else}
	<!-- Public, anonymous status page -->
	<div class="public-shell min-h-screen bg-white dark:bg-zinc-900 text-gray-900 dark:text-white flex flex-col">
		<header class="border-b border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
			<div class="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
				<button class="flex items-center gap-3" on:click={() => push("/")} title={site.name}>
					<Img class="h-7 w-auto max-w-none dark:invert" src="/LogoTextWide-Black.png" alt="Abstract" />
					<span class="text-lg font-bold">{site.name}</span>
				</button>
				<div class="flex items-center gap-2">
					<a href="/Incidents" use:link class="text-sm text-gray-500 hover:text-gray-900 dark:hover:text-white">Incident History</a>
					<Button size="xs" color="alternative" class="p-2" on:click={toggleTheme}>
						<Icon name={$colorTheme === "dark" ? "sun-solid" : "moon-solid"} class="w-4 h-4" />
					</Button>
				</div>
			</div>
		</header>

		<main class="flex-1 w-full max-w-3xl mx-auto px-4 py-8">
			<Router {routes} restoreScrollState={true} on:conditionsFailed={() => push("/")} />
		</main>

		<footer class="border-t border-gray-200 dark:border-zinc-800 py-6">
			<div class="max-w-3xl mx-auto px-4 text-center text-xs text-gray-400">
				<p>Powered by Abstract Status</p>
			</div>
		</footer>
	</div>
{/if}
